// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import * as sharedSelectors from "@modules/shared/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Button } from "@components";
import { AlertAbsenceReasonsForm } from "@modules/service/components";
import type { StepProps } from "@components";
import type { Alert, AbsenceReason } from "@modules/shared/reducer/absence-reasons/types";

type StepData = $Exact<{
  absenceReasons: ?Array<Object>,
}>;

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Alert,
  reasons: Array<AbsenceReason>,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepAbsenceReasons extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    this.validate = new Ajv().compile(this.schema);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.schema && prevProps.schema !== this.schema) {
      this.validate = new Ajv().compile(this.schema);
    }
  }

  get data(): StepData {
    return { absenceReasons: (this.props.draft || {}).content.absenceReasonScopes || null };
  }

  get schema() {
    return {
      ...(this.props.schema || {}).properties.absenceReasonScopes,
    };
  }

  isValid = (data: StepData) => {
    if (!this.validate) {
      return false;
    }

    const isValid = data.absenceReasons === null || this.validate(data.absenceReasons);
    if (!isValid) {
      console.warn("isValid errors", this.validate.errors, data.absenceReasons, this.schema);
    }

    return isValid;
  };

  handleSubmit = () => {
    if (this.props.handleSubmit) {
      this.props.setDraft({
        ...this.props.draft,
        absenceReasonScopes: this.data.absenceReasons,
      });
      this.props.handleSubmit({}, true);
    }
  };

  updateAbsenceReasons = (data: Object) => {
    let manipulatedDraft = this.props.draft?.content;

    if ((manipulatedDraft || {}).details.alertType === "ABSENCE_REASON_ALERT_TYPE") {
      manipulatedDraft = {
        ...manipulatedDraft,
        details: {
          ...(manipulatedDraft || {}).details,
          absenceReasonAlertType: {
            absenceReasons: data.absenceReasons,
          },
        },
      };
    }

    if ((manipulatedDraft || {}).details.alertType === "X_SPELLS_OF_SAME_REASON_IN_Y_PERIOD_TYPE") {
      manipulatedDraft = {
        ...manipulatedDraft,
        details: {
          ...(manipulatedDraft || {}).details,
          xSpellsOfSameReasonInYPeriodType: {
            ...((manipulatedDraft || {}).details || {}).xSpellsOfSameReasonInYPeriodType,
            absenceReasons: data.absenceReasons,
          },
        },
      };
    }

    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        ...manipulatedDraft,
        absenceReasonScopes: data.absenceReasons,
      },
    });
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    const reasonsAreMandatory =
      ((this.props.draft || {}).details || {}).alertType === "ABSENCE_REASON_ALERT_TYPE" ||
      ((this.props.draft || {}).details || {}).alertType ===
        "X_SPELLS_OF_SAME_REASON_IN_Y_PERIOD_TYPE";

    return (
      <React.Fragment>
        <div className="w-100">
          {this.schema && this.data ? (
            <AlertAbsenceReasonsForm
              data={this.data}
              options={this.props.reasons}
              reasonsAreMandatory={reasonsAreMandatory}
              onChange={this.updateAbsenceReasons}
            />
          ) : null}
        </div>
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button
              materialIcon="fast_forward"
              onClick={this.handleSubmit}
              disabled={!this.isValid(this.data)}
            >
              {this.props.t("NEXT")}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAlertSchema(state),
    draft: serviceSelectors.getAlertDraft(state),
    reasons: sharedSelectors.getAbsenceReasons(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAlertDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(StepAbsenceReasons);
